import {
  type ConsultationRequestDTO,
  ConsultationRequestStatusDTO,
  ConsultationRequestUserRelationDTO,
  type ErrorResponseDto,
  ErrorResponseDtoCodeEnum,
  EscalationLevelDTO,
  UrgencyDTO
} from '@/generated'
import { computed, type Ref } from 'vue'
import { ConsultationService } from '@/services/telehealthApi/ConsultationService'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { useUserStore } from '@/stores/user'
import { useConsultationRequestStore } from '@/stores/consultationRequest'
import { isAxiosError } from 'axios'
import { useI18n } from 'vue-i18n'

const declinableEscalationLevels: EscalationLevelDTO[] = [
  EscalationLevelDTO.FIRST_SHIFT,
  EscalationLevelDTO.BACKUP_SHIFT,
  EscalationLevelDTO.TCC
]

const deletableStatuses: ConsultationRequestStatusDTO[] = [
  ConsultationRequestStatusDTO.CREATED,
  ConsultationRequestStatusDTO.ACCEPTED,
  ConsultationRequestStatusDTO.IN_PROGRESS
]

export function useConsultationRequest(consultationRequest: Ref<ConsultationRequestDTO>) {
  const { t } = useI18n()

  const noticeStore = useNoticeStore()
  const { refreshIncomingConsultationRequestList, refreshOutgoingConsultationRequestList } =
    useConsultationRequestStore()
  const { hasChiefPhysicianShift, isRoutingV2Enabled } = useUserStore()

  const caseId = computed<string>(() => consultationRequest.value.case.id)
  const medicalHistory = computed<string | undefined | null>(
    () => consultationRequest.value.medicalHistory
  )
  const diagnosis = computed<string>(() => consultationRequest.value.diagnosis)
  const therapy = computed<string | undefined | null>(() => consultationRequest.value.therapy)
  const question = computed<string>(() => consultationRequest.value.question)
  const consultationId = computed<string>(() => consultationRequest.value.id)

  const isOutgoingRequest = computed<boolean>(
    () => consultationRequest.value.relationToUser === ConsultationRequestUserRelationDTO.OUTGOING
  )

  const isIncomingRequest = computed<boolean>(
    () => consultationRequest.value.relationToUser === ConsultationRequestUserRelationDTO.INCOMING
  )

  const isEmergencyRequest = computed<boolean>(
    () => consultationRequest.value.urgency === UrgencyDTO.EMERGENCY
  )

  const isStatusCreated = computed<boolean>(
    () => consultationRequest.value.status === ConsultationRequestStatusDTO.CREATED
  )

  const isStatusAccepted = computed<boolean>(
    () => consultationRequest.value.status === ConsultationRequestStatusDTO.ACCEPTED
  )

  const isStatusInProgress = computed<boolean>(
    () => consultationRequest.value.status === ConsultationRequestStatusDTO.IN_PROGRESS
  )

  const isStatusReportPending = computed<boolean>(
    () => consultationRequest.value.status === ConsultationRequestStatusDTO.REPORT_PENDING
  )

  const isStatusCompleted = computed<boolean>(
    () => consultationRequest.value.status === ConsultationRequestStatusDTO.COMPLETED
  )

  const isStatusDeleted = computed<boolean>(
    () => consultationRequest.value.status === ConsultationRequestStatusDTO.DELETED
  )

  const isStatusPreCall = computed<boolean>(() => isStatusCreated.value || isStatusAccepted.value)

  const isAssigned = computed<boolean>(() => !!consultationRequest.value.assignedTo)

  const isFinishedConsultation = computed<boolean>(() => !isStatusInProgress.value)

  const userCanAccept = computed<boolean>(() => {
    if (isEmergencyRequest.value) {
      return false
    }

    return isIncomingRequest.value && isStatusCreated.value
  })

  const userCanDecline = computed<boolean>(() => {
    if (isEmergencyRequest.value) {
      return false
    }

    return (
      isIncomingRequest.value &&
      isStatusPreCall.value &&
      (isRoutingV2Enabled ||
        declinableEscalationLevels.includes(consultationRequest.value.escalationLevel))
    )
  })

  const userCanDelete = computed<boolean>(() => {
    return (
      hasChiefPhysicianShift ||
      (deletableStatuses.includes(consultationRequest.value.status) && isOutgoingRequest.value)
    )
  })

  const userCanStartCall = computed<boolean>(() => {
    if (isStatusCompleted.value || isStatusDeleted.value) {
      return false
    }

    if (isEmergencyRequest.value) {
      return isIncomingRequest.value || isOutgoingRequest.value
    }

    if (isStatusCreated.value) {
      return false
    }

    return isIncomingRequest.value || isOutgoingRequest.value
  })

  const userCanWriteReport = computed<boolean>(() => {
    if (isEmergencyRequest.value) {
      return isStatusReportPending.value && isIncomingRequest.value
    }

    return (
      isIncomingRequest.value &&
      (isStatusAccepted.value || isStatusInProgress.value || isStatusReportPending.value)
    )
  })

  async function refreshAfterMutation(): Promise<void> {
    if (isIncomingRequest.value) {
      await refreshIncomingConsultationRequestList()
    }

    if (isOutgoingRequest.value) {
      await refreshOutgoingConsultationRequestList()
    }
  }

  async function declineConsultationRequest(): Promise<void> {
    try {
      await ConsultationService.declineConsultationRequest(consultationId.value)
      await refreshAfterMutation()
    } catch (error) {
      if (isAxiosError<ErrorResponseDto>(error) && error.response?.data) {
        const errorData = error.response.data

        if (errorData.code === ErrorResponseDtoCodeEnum.NO_AVAILABLE_PHYSICIAN) {
          noticeStore.add({
            type: NoticeType.ERROR,
            message: t(
              'You cannot decline this consultation request, because there is currently no physician available to take over. Please finalize this consultation request.'
            )
          })
          return
        }

        if (errorData.code === ErrorResponseDtoCodeEnum.VALIDATION_FAILED) {
          noticeStore.add({
            type: NoticeType.ERROR,
            message: t('Consultation validation failed during decline')
          })
          return
        }
      }

      noticeStore.add({
        type: NoticeType.ERROR,
        message: t('Declining the consultation request {id} failed'),
        messageParameters: { id: consultationId.value }
      })
    }
  }

  async function acceptConsultationRequest(): Promise<void> {
    try {
      await ConsultationService.acceptConsultationRequest(consultationId.value)
      await refreshAfterMutation()
    } catch (error) {
      if (isAxiosError<ErrorResponseDto>(error) && error.response?.data) {
        const errorData = error.response.data

        if (errorData.code === ErrorResponseDtoCodeEnum.VALIDATION_FAILED) {
          noticeStore.add({
            type: NoticeType.ERROR,
            message: t('Consultation validation failed during acceptance')
          })
          return
        }
      }

      noticeStore.add({
        type: NoticeType.ERROR,
        message: t('Accepting the consultation request {id} failed'),
        messageParameters: { id: consultationId.value }
      })
    }
  }

  return {
    caseId,
    medicalHistory,
    diagnosis,
    therapy,
    question,
    consultationId,
    isAssigned,
    isFinishedConsultation,
    isOutgoingRequest,
    isIncomingRequest,
    isEmergencyRequest,
    isStatusCreated,
    isStatusAccepted,
    isStatusInProgress,
    isStatusReportPending,
    isStatusCompleted,
    isStatusDeleted,
    isStatusPreCall,
    userCanAccept,
    userCanDecline,
    userCanStartCall,
    userCanWriteReport,
    userCanDelete,
    acceptConsultationRequest,
    declineConsultationRequest
  }
}
